import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Button, Callout, Card } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import { ATTENTION_SHOWN, newestFirst, shownRows } from './attention-list.js';

/**
 * Card payments that need a person (docs/PAYMENTS.md flow 7, criteria 25–26).
 *
 * Shown on Sales only when there is something: a payment keyed on a terminal
 * without Shekko (the outage case — usually matched to the card a manager
 * recorded by hand), a refund made in BlueSage's back office, a payment
 * approved for a different amount, or one still unknown after five minutes.
 *
 * The newest few, until it is opened (attention-list.ts): a long list ran the
 * page on for screens.
 */
export function CardAttention() {
  const attention = useQuery({
    queryKey: ['payments', 'attention'],
    queryFn: () => api.payments.attention.query(),
    refetchInterval: 60_000,
  });
  const [open, setOpen] = useState(false);
  const data = attention.data;
  if (data === undefined || (data.unmatched.length === 0 && data.flagged.length === 0)) return null;
  const rows = newestFirst(data.unmatched, data.flagged, {
    unmatched: (item) => item.occurredAt,
    flagged: (item) => item.createdAt,
  });
  const hidden = rows.length - shownRows(rows, false).length;
  const toggle =
    hidden > 0 ? (
      <Button variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? `Show the newest ${ATTENTION_SHOWN}` : `Show all ${rows.length}`}
      </Button>
    ) : null;
  return (
    <Card title={`Card payments to sort out (${rows.length})`} actions={toggle}>
      <ul className="card-attention">
        {shownRows(rows, open).map((row) =>
          row.kind === 'unmatched' ? (
            <UnmatchedRow key={row.item.id} item={row.item as Attention['unmatched'][number]} />
          ) : (
            <FlaggedRow key={row.item.id} item={row.item as Attention['flagged'][number]} />
          ),
        )}
      </ul>
      {!open && hidden > 0 ? (
        <p className="text-secondary card-attention__more">
          {hidden} older {hidden === 1 ? 'one' : 'ones'} not shown.
        </p>
      ) : null}
    </Card>
  );
}

type Attention = NonNullable<Awaited<ReturnType<typeof api.payments.attention.query>>>;

function card(brand: string | null, last4: string | null): string {
  return `${brand === null ? 'Card' : brand.charAt(0).toUpperCase() + brand.slice(1)}${last4 === null ? '' : ` •${last4}`}`;
}

function UnmatchedRow({ item }: { item: Attention['unmatched'][number] }) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [note, setNote] = useState('');
  const [approving, setApproving] = useState(false);
  const resolve = useMutation({
    mutationFn: (input: {
      resolution: 'attach' | 'refund' | 'accounted_for';
      tenderId?: string;
      override?: ManagerOverride;
    }) =>
      api.payments.resolveUnmatched.mutate({
        eventId: item.id,
        resolution: input.resolution,
        note,
        ...(input.tenderId === undefined ? {} : { tenderId: input.tenderId }),
        ...(input.override === undefined ? {} : { override: input.override }),
      }),
    onSuccess: async (_result, input) => {
      setApproving(false);
      notify({
        title:
          input.resolution === 'attach'
            ? 'Matched to its sale'
            : input.resolution === 'refund'
              ? 'Refunded to the card'
              : 'Marked accounted for',
      });
      await queryClient.invalidateQueries({ queryKey: ['payments', 'attention'] });
    },
    onError: (error, input) => {
      if (input.resolution === 'refund' && isOverridable(error)) setApproving(true);
    },
  });

  const amount = item.amountCents === null ? 'A payment' : money(item.amountCents);
  return (
    <li className="card-attention__item">
      <p>
        <strong>
          {amount} on {card(item.cardBrand, item.cardLast4)}
        </strong>{' '}
        <span className="text-secondary">
          {new Date(item.occurredAt).toLocaleString()}
          {item.terminal === null ? '' : ` · ${item.terminal}`}
          {item.jobCode === null ? '' : ` · typed: ${item.jobCode}`}
        </span>
      </p>
      <p className="text-secondary">
        {item.kind === 'unmatched'
          ? 'Taken on a terminal without Shekko — when the register was down, most likely. Match it to the card payment recorded by hand for it, or refund it.'
          : 'A refund made in BlueSage’s back office, not in Shekko. Check it, then mark it accounted for.'}
      </p>
      <div className="card-attention__actions">
        {item.candidates.map((candidate) => (
          <Button
            key={candidate.tenderId}
            variant="primary"
            busy={resolve.isPending}
            onClick={() => resolve.mutate({ resolution: 'attach', tenderId: candidate.tenderId })}
          >
            It is {candidate.saleNumber ?? 'the sale'}’s {money(candidate.amountCents)}
          </Button>
        ))}
        {item.kind === 'unmatched' ? (
          <Button
            variant="danger"
            busy={resolve.isPending}
            onClick={() => resolve.mutate({ resolution: 'refund' })}
          >
            Refund it to the card
          </Button>
        ) : null}
        <input
          className="input"
          aria-label="A note, for Activity"
          placeholder="A note, for Activity"
          maxLength={300}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <Button
          variant="secondary"
          busy={resolve.isPending}
          disabledReason={note.trim() === '' ? 'Say how it is accounted for' : undefined}
          onClick={() => resolve.mutate({ resolution: 'accounted_for' })}
        >
          Accounted for
        </Button>
      </div>
      {resolve.isError && !isOverridable(resolve.error) ? (
        <Callout tone="danger">{errorMessage(resolve.error)}</Callout>
      ) : null}
      {approving ? (
        <ManagerApproval
          title="Manager approval needed"
          because="Refunding a payment gives money back to the card."
          busy={resolve.isPending}
          error={resolve.isError ? approvalRefusal(resolve.error) : null}
          onCancel={() => setApproving(false)}
          onApprove={(override) => resolve.mutate({ resolution: 'refund', override })}
        />
      ) : null}
    </li>
  );
}

function FlaggedRow({ item }: { item: Attention['flagged'][number] }) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [note, setNote] = useState('');
  const settle = useMutation({
    mutationFn: () => api.payments.settleAttention.mutate({ attemptId: item.id, note }),
    onSuccess: async () => {
      notify({ title: 'Marked sorted out' });
      await queryClient.invalidateQueries({ queryKey: ['payments', 'attention'] });
    },
  });
  const unknown = item.status === 'unknown' && item.attention === null;
  return (
    <li className="card-attention__item">
      <p>
        <strong>
          {money(item.amountCents)} on {card(item.cardBrand, item.cardLast4)}
        </strong>{' '}
        <span className="text-secondary">
          {new Date(item.createdAt).toLocaleString()}
          {item.saleNumber === null ? '' : ` · ${item.saleNumber}`}
        </span>
      </p>
      <p className="text-secondary">
        {unknown
          ? 'Nobody knows yet whether this card was charged. Open the sale at its register to check it again, or have a manager mark it not charged after looking at the terminal.'
          : item.attention}
      </p>
      {unknown ? null : (
        <div className="card-attention__actions">
          <input
            className="input"
            aria-label="What was done, for Activity"
            placeholder="What was done, for Activity"
            maxLength={300}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button
            variant="secondary"
            busy={settle.isPending}
            disabledReason={note.trim() === '' ? 'Say what was done' : undefined}
            onClick={() => settle.mutate()}
          >
            Sorted out
          </Button>
        </div>
      )}
      {settle.isError ? <Callout tone="danger">{errorMessage(settle.error)}</Callout> : null}
    </li>
  );
}
